import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Link } from "react-router-dom";
import {
  getWhatsAppOnboardingStatusAPI,
  submitWhatsAppOnboardingAPI,
  getWhatsAppAccountsAPI,
} from "../../../Redux/ApiCalls/Dashboard/WhatsAppAPI";
import { showErrorToast, showSuccessToast } from "../../atoms/Utlis/Toast";
import LoadingDots from "../../atoms/Utlis/LoadinDots";
import {
  CheckCircle2,
  AlertCircle,
  Phone,
  RefreshCw,
  Send,
  ShieldAlert,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import GradientFillButton from "../../atoms/Utlis/GradientFillButton";
import BackButtonIconPng from "../../../assets/Icons/SingleAlbum/back.png";
import { device } from "../../atoms/Utlis/media";

// ==========================================
// Styled Components
// ==========================================

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled.div`
  width: 100%;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  box-sizing: border-box;
  min-height: calc(100vh - 100px);

  @media ${device.tablet} {
    padding: 20px 16px;
    gap: 18px;
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 360px;
`;

const Hero = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 20px;
  padding: 24px 26px;
  background: linear-gradient(
    135deg,
    rgba(255, 201, 81, 0.18) 0%,
    rgba(206, 86, 176, 0.14) 55%,
    rgba(37, 211, 102, 0.08) 100%
  );
  border: 1px solid rgba(201, 77, 170, 0.12);
  animation: ${fadeUp} 0.35s ease-out;

  @media ${device.tablet} {
    padding: 18px;
  }
`;

const HeroGlow = styled.div`
  pointer-events: none;
  position: absolute;
  top: -45%;
  right: -6%;
  width: 260px;
  height: 260px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(255, 201, 81, 0.35) 0%,
    transparent 70%
  );
`;

const HeroContent = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;

  @media ${device.tablet} {
    flex-direction: column;
  }
`;

const HeroLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  min-width: 0;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;

  &:hover {
    background: #ffffff;
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06);
  }
`;

const HeroText = styled.div`
  min-width: 0;
`;

const HeroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  padding: 5px 11px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.78);
  border: 1px solid rgba(206, 86, 176, 0.2);
  font-family: "Montserrat", sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #ce56b0;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-family: "Urbanist", sans-serif;
  font-size: 28px;
  font-weight: 800;
  color: #171717;
  line-height: 1.15;

  @media ${device.tablet} {
    font-size: 22px;
  }
`;

const PageSubtitle = styled.p`
  margin: 6px 0 0;
  font-family: "Montserrat", sans-serif;
  font-size: 13.5px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.65);
  line-height: 1.45;
`;

const ContentCard = styled.div`
  width: 100%;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.07);
  box-shadow: 0px 4px 24px rgba(0, 0, 0, 0.03);
  border-radius: 20px;
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  box-sizing: border-box;

  @media ${device.tablet} {
    padding: 20px 16px;
  }
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 16px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-family: "Urbanist", sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #171717;
`;

const SectionSubtitle = styled.p`
  margin: -8px 0 0 0;
  font-family: "Montserrat", sans-serif;
  font-size: 13px;
  color: rgba(23, 23, 23, 0.55);
`;

const SubSectionTitle = styled.h3`
  margin: 0;
  font-family: "Urbanist", sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #171717;
`;

const OptionCard = styled.div<{ active: boolean }>`
  border: 2px solid ${props => props.active ? "#ce56b0" : "rgba(0, 0, 0, 0.06)"};
  background: ${props => props.active ? "rgba(206, 86, 176, 0.03)" : "#ffffff"};
  border-radius: 16px;
  padding: 20px;
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  transition: all 0.25s ease;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.02);

  &:hover {
    border-color: #ce56b0;
    transform: translateY(-2px);
  }
`;

const RadioCircle = styled.div<{ active: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid ${props => props.active ? "#ce56b0" : "rgba(0, 0, 0, 0.2)"};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
  flex-shrink: 0;

  &::after {
    content: "";
    display: ${props => props.active ? "block" : "none"};
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background-color: #ce56b0;
  }
`;

const OptionTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const OptionTitle = styled.h4`
  margin: 0;
  font-family: "Urbanist", sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #171717;
`;

const OptionDesc = styled.p`
  margin: 0;
  font-family: "Montserrat", sans-serif;
  font-size: 13px;
  font-weight: 500;
  color: rgba(23, 23, 23, 0.55);
`;

const InputField = styled.input`
  width: 100%;
  padding: 14px 18px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  font-family: "Montserrat", sans-serif;
  font-size: 15px;
  outline: none;
  transition: all 0.2s;
  background-color: #fcfcfc;

  &:focus {
    border-color: #ce56b0;
    background-color: #ffffff;
    box-shadow: 0 0 0 3px rgba(206, 86, 176, 0.12);
  }
`;

const OnboardingForm = styled.form`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 24px;
`;

const Badge = styled.span<{ type: "active" | "inactive" }>`
  padding: 6px 14px;
  border-radius: 20px;
  font-family: "Urbanist", sans-serif;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: fit-content;

  ${props =>
    props.type === "active"
      ? `
      background-color: rgba(34, 197, 94, 0.1);
      color: rgb(21, 128, 61);
      border: 1px solid rgba(34, 197, 94, 0.2);
    `
      : `
      background-color: rgba(234, 179, 8, 0.1);
      color: rgb(161, 98, 7);
      border: 1px solid rgba(234, 179, 8, 0.2);
    `}
`;

const StatusRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
`;

const StatusSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const RefreshButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #ffffff;
  border: 1px solid rgba(206, 86, 176, 0.25);
  cursor: pointer;
  font-family: "Urbanist", sans-serif;
  font-size: 13.5px;
  font-weight: 700;
  color: #ce56b0;
  padding: 8px 14px;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(206, 86, 176, 0.08);
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(206, 86, 176, 0.06);
    border-color: rgba(206, 86, 176, 0.4);
    transform: translateY(-1px);
  }
`;

const ConnectedCard = styled.div`
  background: linear-gradient(135deg, rgba(255, 201, 81, 0.08) 0%, rgba(206, 86, 176, 0.08) 100%);
  border: 1px dashed rgba(206, 86, 176, 0.35);
  border-radius: 20px;
  padding: 26px 28px;
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media ${device.tablet} {
    padding: 20px 16px;
  }
`;

const ConnectedCardSolid = styled(ConnectedCard)`
  border-style: solid;
`;

const CardHeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconCircle = styled.div<{ $color: string }>`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background-color: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const CardInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CardInfoTitle = styled.h4`
  margin: 0;
  font-family: "Urbanist", sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #171717;
`;

const CardInfoSubtitle = styled.p`
  margin: 0;
  font-family: "Montserrat", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #4d4d4d;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
`;

const MetaItem = styled.div`
  background: #ffffff;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const MetaLabel = styled.span`
  font-family: "Montserrat", sans-serif;
  font-size: 12px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.4);
  text-transform: uppercase;
`;

const MetaValue = styled.span`
  font-family: "Urbanist", sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #171717;
`;

const MetaValueSmall = styled(MetaValue)`
  font-size: 13px;
  word-break: break-all;
`;

const InfoBanner = styled.div<{ $variant: "warning" | "success" }>`
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  background: ${props => props.$variant === "warning" ? "rgba(234, 179, 8, 0.04)" : "rgba(34, 197, 94, 0.04)"};
  border: 1px solid ${props => props.$variant === "warning" ? "rgba(234, 179, 8, 0.15)" : "rgba(34, 197, 94, 0.15)"};
  border-radius: 16px;
`;

const InfoBannerIcon = styled.div`
  flex-shrink: 0;
  margin-top: 2px;
`;

const InfoBannerContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InfoBannerTitle = styled.span`
  font-family: "Urbanist", sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #171717;
`;

const InfoBannerText = styled.span`
  font-family: "Montserrat", sans-serif;
  font-size: 13px;
  color: rgba(23, 23, 23, 0.65);
  line-height: 1.4;
`;

const LinkedNumbersSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LinkedNumbersLabel = styled.span`
  font-family: "Urbanist", sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #171717;
`;

const AccountRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #ffffff;
  padding: 14px 20px;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.05);
`;

const PhoneText = styled.span`
  font-family: "Urbanist", sans-serif;
  font-size: 15px;
  font-weight: 700;
  color: #171717;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DefaultBadge = styled.span`
  font-family: "Montserrat", sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: #ce56b0;
  background: rgba(206, 86, 176, 0.1);
  padding: 2px 8px;
  border-radius: 6px;
  text-transform: uppercase;
`;

const SendIconWrapper = styled.div`
  flex-shrink: 0;
  margin-top: 2px;
  transform: rotate(-20deg);
`;

export const WhatsAppOnboardingPage: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [data, setData] = useState<any>(null);
  const [connectionDetails, setConnectionDetails] = useState<any>(null);

  // Form states
  const [useExisting, setUseExisting] = useState<boolean>(true);
  const [newMobileNumber, setNewMobileNumber] = useState<string>("");

  const loadOnboardingStatus = async (showToast: boolean = false) => {
    setLoading(true);
    try {
      const res = await getWhatsAppOnboardingStatusAPI();
      setData(res);

      if (res?.status === "active") {
        const connRes = await getWhatsAppAccountsAPI();
        setConnectionDetails(connRes);
      }
      if (showToast) {
        showSuccessToast("Status refreshed successfully!");
      }
    } catch (err: any) {
      showErrorToast(err?.message || "Failed to fetch onboarding status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOnboardingStatus();
  }, []);

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setSubmitting(true);

    try {
      const payload: any = {
        use_existing_studio_number: useExisting,
      };

      if (!useExisting) {
        if (!newMobileNumber) {
          showErrorToast("Please enter a new mobile number");
          setSubmitting(false);
          return;
        }
        // Match country code prefix (only digits, eg. 919876543210)
        if (!/^\d+$/.test(newMobileNumber)) {
          showErrorToast("mobile_number must include country code (e.g. 919876543210)");
          setSubmitting(false);
          return;
        }
        payload.mobile_number = newMobileNumber;
      } else {
        if (!data?.studio_owner_mobile) {
          showErrorToast("Studio owner phone number is not set. Provide a mobile_number instead");
          setSubmitting(false);
          return;
        }
      }

      const res = await submitWhatsAppOnboardingAPI(payload);
      showSuccessToast(res?.message || "WhatsApp details submitted successfully.");
      loadOnboardingStatus();
    } catch (err: any) {
      showErrorToast(err?.message || "Something went wrong during submission");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !data) {
    return (
      <Container>
        <Hero>
          <HeroGlow />
          <HeroContent>
            <HeroLeft>
              <BackLink to="/dashboard/whatsapp" aria-label="Go back">
                <img
                  src={BackButtonIconPng}
                  style={{ width: "19.6px", height: "16.8px" }}
                  alt="Back"
                />
              </BackLink>
              <HeroText>
                <HeroBadge>
                  <Sparkles size={13} />
                  WhatsApp CRM
                </HeroBadge>
                <PageTitle className="linear-text">
                  WhatsApp Business API Onboarding
                </PageTitle>
                <PageSubtitle>
                  Integrate your business WhatsApp account using MSG91 configuration to seamlessly communicate with your studio customers, share albums, send QR codes, and more.
                </PageSubtitle>
              </HeroText>
            </HeroLeft>
          </HeroContent>
        </Hero>
        <ContentCard>
          <LoadingWrapper>
            <LoadingDots />
          </LoadingWrapper>
        </ContentCard>
      </Container>
    );
  }

  const hasOnboarding = !!data?.onboarding;
  const status = data?.status || "inactive";

  return (
    <Container>
      <Hero>
        <HeroGlow />
        <HeroContent>
          <HeroLeft>
            <BackLink to="/dashboard/whatsapp" aria-label="Go back">
              <img
                src={BackButtonIconPng}
                style={{ width: "19.6px", height: "16.8px" }}
                alt="Back"
              />
            </BackLink>
            <HeroText>
              <HeroBadge>
                <Sparkles size={13} />
                WhatsApp CRM
              </HeroBadge>
              <PageTitle className="linear-text">
                WhatsApp Business API Onboarding
              </PageTitle>
              <PageSubtitle>
                Integrate your business WhatsApp account using MSG91 configuration to seamlessly communicate with your studio customers, share albums, send QR codes, and more.
              </PageSubtitle>
            </HeroText>
          </HeroLeft>
        </HeroContent>
      </Hero>

      <ContentCard>
        {!hasOnboarding && status === "inactive" && (
          <OnboardingForm onSubmit={handleSubmit}>
            <Section>
              <SectionTitle>Choose WhatsApp Mobile Number</SectionTitle>
              <SectionSubtitle>
                Select whether you want to use your registered studio owner number or standard custom number.
              </SectionSubtitle>

              <OptionsGrid>
                <OptionCard active={useExisting} onClick={() => setUseExisting(true)}>
                  <RadioCircle active={useExisting} />
                  <OptionTextContainer>
                    <OptionTitle>Use registered studio number</OptionTitle>
                    <OptionDesc>
                      Uses your profile phone number: <strong>{data?.studio_owner_mobile || "Not Set"}</strong>
                    </OptionDesc>
                  </OptionTextContainer>
                </OptionCard>

                <OptionCard active={!useExisting} onClick={() => setUseExisting(false)}>
                  <RadioCircle active={!useExisting} />
                  <OptionTextContainer>
                    <OptionTitle>Use a new mobile number</OptionTitle>
                    <OptionDesc>Provide a custom mobile number dedicated for WhatsApp communications.</OptionDesc>
                  </OptionTextContainer>
                </OptionCard>
              </OptionsGrid>
            </Section>

            {!useExisting && (
              <Section>
                <SubSectionTitle>Custom Mobile Number</SubSectionTitle>
                <div style={{ maxWidth: "480px" }}>
                  <InputField
                    placeholder="e.g. 919876543210 (with country code, no +, no spaces)"
                    value={newMobileNumber}
                    onChange={(e) => setNewMobileNumber(e.target.value)}
                  />
                </div>
              </Section>
            )}

            <div style={{ maxWidth: "340px" }}>
              <GradientFillButton
                onClick={submitting ? undefined : () => handleSubmit()}
                className={`w-full flex items-center justify-center gap-2 py-3 mt-2 text-base font-semibold ${submitting ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {submitting ? "Submitting..." : <>Submit Details <ArrowRight size={18} /></>}
              </GradientFillButton>
            </div>
          </OnboardingForm>
        )}

        {hasOnboarding && status === "inactive" && (
          <StatusSection>
            <StatusRow>
              <Badge type="inactive">
                <AlertCircle size={16} /> Verification Pending
              </Badge>
              <RefreshButton onClick={() => loadOnboardingStatus(true)}>
                <RefreshCw size={15} /> Refresh Status
              </RefreshButton>
            </StatusRow>

            <ConnectedCard>
              <CardHeaderRow>
                <IconCircle $color="rgba(234, 179, 8, 0.1)">
                  <Phone size={20} color="rgb(161, 98, 7)" />
                </IconCircle>
                <CardInfo>
                  <CardInfoTitle>Submitted Number</CardInfoTitle>
                  <CardInfoSubtitle>+{data.onboarding.mobile_number}</CardInfoSubtitle>
                </CardInfo>
              </CardHeaderRow>

              <MetaGrid>
                <MetaItem>
                  <MetaLabel>Submitted At</MetaLabel>
                  <MetaValue>{new Date(data.onboarding.created_at).toLocaleDateString()}</MetaValue>
                </MetaItem>
                <MetaItem>
                  <MetaLabel>Request ID</MetaLabel>
                  <MetaValueSmall>{data.onboarding.id}</MetaValueSmall>
                </MetaItem>
              </MetaGrid>
            </ConnectedCard>

            <InfoBanner $variant="warning">
              <InfoBannerIcon>
                <ShieldAlert size={20} color="rgb(161, 98, 7)" />
              </InfoBannerIcon>
              <InfoBannerContent>
                <InfoBannerTitle>What's next?</InfoBannerTitle>
                <InfoBannerText>
                  Our superadmin team is configuring the MSG91 API gateway for your number. Once setup is complete, your status will be updated to active.
                </InfoBannerText>
              </InfoBannerContent>
            </InfoBanner>
          </StatusSection>
        )}

        {status === "active" && (
          <StatusSection>
            <StatusRow>
              <Badge type="active">
                <CheckCircle2 size={16} /> Live &amp; Active
              </Badge>
              <RefreshButton onClick={() => loadOnboardingStatus(true)}>
                <RefreshCw size={15} /> Refresh Info
              </RefreshButton>
            </StatusRow>

            <ConnectedCardSolid>
              <CardHeaderRow>
                <IconCircle $color="rgba(34, 197, 94, 0.1)">
                  <CheckCircle2 size={20} color="rgb(21, 128, 61)" />
                </IconCircle>
                <CardInfo>
                  <CardInfoTitle>Integrated WhatsApp Mobile</CardInfoTitle>
                  <CardInfoSubtitle>
                    +{connectionDetails?.mobile_number || data?.onboarding?.mobile_number}
                  </CardInfoSubtitle>
                </CardInfo>
              </CardHeaderRow>

              {connectionDetails?.phone_numbers && connectionDetails.phone_numbers.length > 0 && (
                <LinkedNumbersSection>
                  <LinkedNumbersLabel>Linked Number(s)</LinkedNumbersLabel>
                  {connectionDetails.phone_numbers.map((pn: any) => (
                    <AccountRow key={pn.id}>
                      <PhoneText>
                        <Phone size={14} color="#ce56b0" /> +{pn.integrated_number}
                      </PhoneText>
                      {pn.is_default && <DefaultBadge>Default</DefaultBadge>}
                    </AccountRow>
                  ))}
                </LinkedNumbersSection>
              )}
            </ConnectedCardSolid>

            <InfoBanner $variant="success">
              <InfoBannerIcon>
                <SendIconWrapper>
                  <Send size={20} color="rgb(21, 128, 61)" />
                </SendIconWrapper>
              </InfoBannerIcon>
              <InfoBannerContent>
                <InfoBannerTitle>Ready to go!</InfoBannerTitle>
                <InfoBannerText>
                  Your business is now fully connected to the WhatsApp API. You can start sending digital media link cards, automated templates, and customer invoices directly.
                </InfoBannerText>
              </InfoBannerContent>
            </InfoBanner>
          </StatusSection>
        )}
      </ContentCard>
    </Container>
  );
};

export default WhatsAppOnboardingPage;
